/**
 * +acolyte-specs (S7, docs/design/classes/specs.md "Sim features"): friendly summons (pets).
 *
 * A `summon` effect with `pet` (the Heretic's Hollow One) spawns the NPC on the caster's side: faction
 * 'party', no AI brain and no threat table (enemies never fight it), and it never takes damage. Every
 * tick the pet picks its owner's hostile target (else the nearest enemy fighting its owner), runs into
 * melee and attacks every `intervalMs` (hasted by the owner) by running `attackAbilityId`'s effects as
 * its OWNER (source = owner, so the damage, threat, procs and curses are the owner's; point = the pet).
 * With nothing to fight it follows its owner. It despawns when its time is up or its owner dies.
 * Pure state on the unit (`Unit.pet`): snapshots, replays and the network carry it like any field.
 *
 * +ranger-specs: the same system's persistent mode (`summon` with `companion`, `Unit.companion`, at
 * the end of this file): a pet that is a real fighter (own health, swings, threat, death and
 * re-forming, commands and the Bond sync), used by the Beastbound's spirit beast.
 */
import type { AbilityId, CompanionCastEffect, CompanionDef, DamageRequest, EffectCtx, SimContext, SummonEffect, Unit, UnitId, Vec2 } from '../contracts';
import { MELEE_RANGE_BONUS, MIN_MELEE_RANGE, TICK_MS } from '../contracts';
import { applyAuraDef, removeInstance } from './auras'; // +ranger-specs
import { changeResource } from './resources'; // +ranger-specs
import { runEffects } from './effects';
import { unitEffectiveStats } from './stats';
import { isHostile, unitOf } from './util';

/** The pet stops this close behind its owner when it has nothing to fight. */
const FOLLOW_YD = 3;
/** It looks for a fight this far from itself when its owner has no hostile target. */
const ASSIST_YD = 20;
/** It never chases further than this from its owner. */
const LEASH_YD = 40;

const dist = (a: Vec2, b: Vec2): number => Math.hypot(a.x - b.x, a.y - b.y);

/** Spawn the pets of a `summon` effect with `pet` (replaces the caster's earlier ones of that NPC). */
export function spawnPets(ctx: SimContext, e: SummonEffect, owner: Unit, center: Vec2, abilityId: string): void {
  const spec = e.pet!;
  for (const id of [...ctx.world.unitOrder]) {
    const old = ctx.world.units[id];
    if (old?.pet && old.pet.ownerId === owner.id && old.npcId === e.npcId) ctx.ops.despawn(old.id);
  }
  // Around a target the pet claws through on the owner's side of it (in view, already in reach).
  const start = e.anchor === 'target' ? owner.facing + Math.PI * 0.8 : owner.facing;
  for (let i = 0; i < e.count; i++) {
    const ang = start + (2 * Math.PI * i) / Math.max(1, e.count);
    const pos = ctx.sys.spatial.clampToWalkable(ctx.world, { x: center.x + Math.cos(ang) * e.radius, y: center.y + Math.sin(ang) * e.radius });
    const n = ctx.ops.spawnNpc({ npcId: e.npcId, pos, facing: owner.facing, packId: null, summonerId: owner.id });
    n.faction = owner.faction;
    n.ai = null;
    n.threat = null;
    n.swing = null;
    n.meleeDamage = null;
    n.ccImmune = true;
    if (owner.level !== undefined) n.level = owner.level;
    n.pet = { ownerId: owner.id, expiresAt: ctx.now + spec.durationMs, attackAbilityId: spec.attackAbilityId, intervalMs: spec.intervalMs, nextAttackAt: ctx.now };
    ctx.emit({ type: 'summon', sourceID: owner.id, targetID: n.id, abilityGameID: abilityId });
  }
}

/** The owner's hostile target, else the nearest living enemy fighting the owner near the pet. */
function petTarget(ctx: SimContext, pet: Unit, owner: Unit): Unit | undefined {
  const t = unitOf(ctx, owner.targetId);
  if (t?.alive && isHostile(owner, t) && dist(t.pos, owner.pos) <= LEASH_YD && (owner.inCombat || t.inCombat)) return t;
  let best: Unit | undefined;
  let bestD = ASSIST_YD;
  for (const id of ctx.world.unitOrder) {
    const u = ctx.world.units[id];
    if (!u?.alive || !isHostile(owner, u) || u.threat?.entries[owner.id] === undefined) continue;
    const d = dist(u.pos, pet.pos);
    if (d < bestD) {
      best = u;
      bestD = d;
    }
  }
  return best;
}

function stop(u: Unit): void {
  if (u.move && u.move.mode !== 'forced') u.move = null;
}

function goTo(ctx: SimContext, u: Unit, p: Vec2): void {
  if (u.move?.mode === 'dest' && dist(u.move.dest, p) <= 1.5) return;
  if (!ctx.sys.spatial.setDestination(ctx, u.id, p)) stop(u);
}

/** Every tick (from tickAuras): expire, follow, fight. */
export function tickPets(ctx: SimContext): void {
  const despawn: UnitId[] = [];
  for (const id of ctx.world.unitOrder) {
    const u = ctx.world.units[id];
    const pet = u?.pet;
    if (!u || !pet) continue;
    const owner = ctx.world.units[pet.ownerId];
    if (!owner?.alive || owner.departed || ctx.now >= pet.expiresAt) {
      despawn.push(u.id);
      continue;
    }
    const target = petTarget(ctx, u, owner);
    if (!target) {
      u.targetId = null;
      u.inCombat = false;
      if (dist(u.pos, owner.pos) > FOLLOW_YD + 1.5) goTo(ctx, u, { x: owner.pos.x - Math.cos(owner.facing) * FOLLOW_YD, y: owner.pos.y - Math.sin(owner.facing) * FOLLOW_YD });
      else stop(u);
      continue;
    }
    u.targetId = target.id;
    u.inCombat = true;
    if (!ctx.sys.spatial.inMeleeRange(ctx.world, u.id, target.id)) {
      goTo(ctx, u, target.pos);
      continue;
    }
    stop(u);
    u.facing = Math.atan2(target.pos.y - u.pos.y, target.pos.x - u.pos.x);
    if (ctx.now < pet.nextAttackAt) continue;
    const ab = ctx.content.ability(pet.attackAbilityId);
    if (!ab) continue;
    pet.nextAttackAt = ctx.now + Math.max(250, Math.round(pet.intervalMs / (1 + unitEffectiveStats(ctx, owner).haste)));
    ctx.emit({ type: 'cast', sourceID: u.id, targetID: target.id, abilityGameID: ab.id });
    runEffects(ctx, ab.effects, { sourceId: owner.id, abilityId: ab.id, targetId: target.id, point: { ...u.pos }, depth: 1 });
  }
  for (const id of despawn) ctx.ops.despawn(id);
}

/*
 * ===========================================================================
 * +ranger-specs (S7, persistent pets: "companions"), docs/design/classes/specs.md "Beastbound"): persistent companions.
 *
 * The Beastbound's spirit beast is a real unit on its owner's side (unlike the Heretic's guardian
 * pets in pets.ts, which never take damage and strike as their owner): it has its own health (a
 * share of the owner's), swings with its own power (a share of the owner's), generates its own
 * threat, can be attacked, dies and re-forms. Its behaviour lives on its NpcDef (`companion`).
 *
 *  - Summon: a `summon` effect with `companion` (forms by the owner's level: fox, wolf, stag).
 *    `ensure` is the upkeep a passive runs every second: nothing while it lives or re-forms.
 *  - Every tick: re-sync stats from the owner; teleport back when left far behind; out-of-combat
 *    regen; pick a target (Hunt: the owner's target once the owner fights it, else whatever fights
 *    the owner or the beast; Guard: what attacks the owner, staying at heel, taunting on a timer);
 *    run into melee or follow at heel. Swings are ordinary NPC swings (tickMelee).
 *  - Commands: `companionCast` (the beast uses an ability now, optionally leaping in) and
 *    `to: 'companion'` effects (stance auras) — wired in effects.ts.
 *  - Sync: the owner's and the beast's hits on the same target within the window give the owner
 *    Bond (`companionSync`, from the damage pipeline).
 *  - Death: the owner gets the re-forming aura; the passive summons a new one when it ends (it
 *    drops out of combat). The owner dying or leaving takes the beast with it.
 *
 * Deterministic, pure state on the unit (`Unit.companion`), serialisable like any field.
 */

/** Heel: this far behind and beside the owner. */
const HEEL_BACK = 2.2;
const HEEL_SIDE = 1.4;
/** Re-path toward heel once this far from it. */
const HEEL_SLACK = 1.6;
/** Left this far behind (a portal, a long dash, a respawn): appear at the owner's side. */
const TELEPORT_YD = 45;
/** Hunt: fights the owner's target within this of the owner; looks for attackers within this. */
const HUNT_LEASH_YD = 40;
const COMPANION_ASSIST_YD = 25;
/** Guard: holds within this of the owner. */
const GUARD_YD = 10;
/** Running catch-up beyond this from its owner. */
const CATCH_UP_YD = 12;
/** Pounce speed (yd/s). */
const LEAP_SPEED = 40;


export function companionDef(ctx: Pick<SimContext, 'content'>, u: Unit): CompanionDef | undefined {
  return u.npcId ? ctx.content.npc(u.npcId)?.companion : undefined;
}

/** Every companion unit (alive or a corpse) of `ownerId`, in unit order. */
function companionsOf(ctx: SimContext, ownerId: UnitId): Unit[] {
  const out: Unit[] = [];
  for (const id of ctx.world.unitOrder) {
    const u = ctx.world.units[id];
    if (u?.companion?.ownerId === ownerId) out.push(u);
  }
  return out;
}

/** The owner's living companion. */
export function companionOf(ctx: SimContext, ownerId: UnitId | null | undefined): Unit | undefined {
  if (!ownerId) return undefined;
  for (const id of ctx.world.unitOrder) {
    const u = ctx.world.units[id];
    if (u?.alive && u.companion?.ownerId === ownerId) return u;
  }
  return undefined;
}

/** The form for the owner's level (highest minLevel ≤ level), else the effect's npcId. */
function formFor(e: SummonEffect, owner: Unit): string {
  let best = e.npcId;
  let bestLvl = -Infinity;
  for (const f of e.companion?.forms ?? []) {
    if ((owner.level ?? 1) >= f.minLevel && f.minLevel > bestLvl) {
      best = f.npcId;
      bestLvl = f.minLevel;
    }
  }
  return best;
}

/** Stats from the owner (power, crit, haste, versatility, max health, run speed), every tick. */
function syncStats(ctx: SimContext, u: Unit, owner: Unit, def: CompanionDef, full = false): void {
  const st = unitEffectiveStats(ctx, owner);
  u.stats.power = st.power * def.powerPct;
  u.stats.crit = st.crit;
  u.stats.haste = st.haste;
  u.stats.versatility = st.versatility;
  if (owner.level !== undefined) u.level = owner.level;
  const maxHp = Math.max(1, Math.round(owner.maxHp * def.hpPct));
  if (maxHp !== u.maxHp) {
    const frac = u.maxHp > 0 ? u.hp / u.maxHp : 1;
    u.maxHp = maxHp;
    u.hp = Math.max(1, Math.min(maxHp, Math.round(maxHp * frac)));
  }
  if (full) u.hp = u.maxHp;
  const run = st.moveSpeed > 0 ? st.moveSpeed : owner.moveSpeed;
  u.moveSpeed = run * (def.speedMult ?? 1.1) * (dist(u.pos, owner.pos) > CATCH_UP_YD ? 1.5 : 1);
}

/** Spawn (or, with `ensure`, keep) the owner's companion. Called by effects.ts summon(). */
export function spawnCompanion(ctx: SimContext, e: SummonEffect, owner: Unit, center: Vec2, abilityId: AbilityId): void {
  const spec = e.companion!;
  const mine = companionsOf(ctx, owner.id);
  if (spec.ensure) {
    if (mine.some((u) => u.alive)) return;
    const formDef = ctx.content.npc(formFor(e, owner))?.companion;
    if (formDef?.reformAuraId && owner.auras.some((a) => a.auraId === formDef.reformAuraId)) return;
  }
  for (const old of mine) ctx.ops.despawn(old.id);
  const npcId = formFor(e, owner);
  const def = ctx.content.npc(npcId)?.companion;
  if (!def) return;
  const ang = owner.facing + Math.PI * 0.75;
  const pos = ctx.sys.spatial.clampToWalkable(ctx.world, { x: center.x + Math.cos(ang) * Math.max(1.5, e.radius), y: center.y + Math.sin(ang) * Math.max(1.5, e.radius) });
  const n = ctx.ops.spawnNpc({ npcId, pos, facing: owner.facing, packId: null, summonerId: owner.id });
  n.faction = owner.faction;
  n.ai = null;
  n.threat = null;
  n.packId = null;
  n.scaling = { hp: 1, damage: 1 };
  n.ccImmune = false;
  n.swing = { speedMs: def.melee.speedMs, nextAt: 0, kind: 'melee' };
  n.meleeDamage = structuredClone(def.melee.damage);
  n.companion = { ownerId: owner.id, ownerHitOn: null, ownerHitAt: 0, hitOn: null, hitAt: 0, lastSyncAt: 0, guardAt: 0 };
  syncStats(ctx, n, owner, def, true);
  ctx.emit({ type: 'summon', sourceID: owner.id, targetID: n.id, abilityGameID: abilityId });
  for (const a of def.auras ?? []) applyAuraDef(ctx, ctx.content.aura(a.id) ?? a, n.id, owner.id, abilityId);
}

/** A hostile, living unit that is fighting the owner or its companion (on its threat table, or targeting them). */
function fightingUs(u: Unit | undefined, owner: Unit, comp: Unit): boolean {
  if (!u?.alive || !isHostile(owner, u)) return false;
  const t = u.threat;
  return !!t && u.inCombat && (t.entries[owner.id] !== undefined || t.entries[comp.id] !== undefined || u.targetId === owner.id || u.targetId === comp.id);
}

function pickTarget(ctx: SimContext, u: Unit, owner: Unit, guard: boolean): Unit | undefined {
  const w = ctx.world;
  const nearest = (ok: (x: Unit) => boolean, from: Vec2, within: number): Unit | undefined => {
    let best: Unit | undefined;
    let bestD = within;
    for (const id of w.unitOrder) {
      const x = w.units[id];
      if (!x || !ok(x)) continue;
      const d = dist(x.pos, from);
      if (d < bestD) {
        best = x;
        bestD = d;
      }
    }
    return best;
  };
  if (guard) {
    // What attacks the owner first; else keep fighting what fights us close by; else the owner's fight at heel.
    const onOwner = nearest((x) => x.alive && isHostile(owner, x) && x.inCombat && x.targetId === owner.id, owner.pos, GUARD_YD + 6);
    if (onOwner) return onOwner;
    const cur = unitOf(ctx, u.targetId);
    if (cur && fightingUs(cur, owner, u) && dist(cur.pos, owner.pos) <= GUARD_YD) return cur;
    const ot = unitOf(ctx, owner.targetId);
    if (ot && fightingUs(ot, owner, u) && dist(ot.pos, owner.pos) <= GUARD_YD) return ot;
    return nearest((x) => fightingUs(x, owner, u), owner.pos, GUARD_YD);
  }
  // Hunt: the owner's target once the owner fights it (a shot, auto-attack, or it fights us).
  const ot = unitOf(ctx, owner.targetId);
  if (ot?.alive && isHostile(owner, ot) && dist(ot.pos, owner.pos) <= HUNT_LEASH_YD && ((owner.autoAttack && ot.inCombat) || fightingUs(ot, owner, u))) return ot;
  const cur = unitOf(ctx, u.targetId);
  if (cur && fightingUs(cur, owner, u) && dist(cur.pos, owner.pos) <= HUNT_LEASH_YD) return cur;
  return nearest((x) => fightingUs(x, owner, u), u.pos, COMPANION_ASSIST_YD);
}

function petStop(u: Unit): void {
  if (u.move && u.move.mode !== 'forced') u.move = null;
}

function petGoTo(ctx: SimContext, u: Unit, p: Vec2): void {
  if (u.move?.mode === 'forced') return;
  if (u.move?.mode === 'dest' && dist(u.move.dest, p) <= 1.2) return;
  if (!ctx.sys.spatial.setDestination(ctx, u.id, p)) petStop(u);
}

function heelPoint(owner: Unit, side: number): Vec2 {
  const f = owner.facing;
  return { x: owner.pos.x - Math.cos(f) * HEEL_BACK + Math.cos(f + Math.PI / 2) * HEEL_SIDE * side, y: owner.pos.y - Math.sin(f) * HEEL_BACK + Math.sin(f + Math.PI / 2) * HEEL_SIDE * side };
}

/** Every tick (from tickAuras, after movement): sync, regen, target, move. */
export function tickCompanions(ctx: SimContext): void {
  const w = ctx.world;
  let gone: UnitId[] | null = null;
  for (const id of w.unitOrder) {
    const u = w.units[id];
    const st = u?.companion;
    if (!u || !st) continue;
    const owner = w.units[st.ownerId];
    if (!owner || owner.departed || !owner.alive) {
      (gone ??= []).push(u.id);
      continue;
    }
    const def = companionDef(ctx, u);
    if (!def) continue;
    if (!u.alive) {
      if (!st.fallen) {
        st.fallen = true;
        const aura = def.reformAuraId ? ctx.content.aura(def.reformAuraId) : undefined;
        if (aura && def.reformMs) applyAuraDef(ctx, aura, owner.id, owner.id, aura.id, { durationMs: owner.inCombat ? def.reformMs : Math.round(def.reformMs / 4) });
      }
      continue;
    }
    syncStats(ctx, u, owner, def);
    if (dist(u.pos, owner.pos) > TELEPORT_YD) {
      u.pos = ctx.sys.spatial.clampToWalkable(w, heelPoint(owner, 1));
      u.move = null;
      u.targetId = null;
    }
    const guard = !!def.guardAuraId && owner.auras.some((a) => a.auraId === def.guardAuraId);
    if (def.guardAura) {
      // The guard stance rides on the companion while its owner holds the command.
      const has = u.auras.find((a) => a.auraId === def.guardAura!.id);
      if (guard && !has) applyAuraDef(ctx, ctx.content.aura(def.guardAura.id) ?? def.guardAura, u.id, owner.id, def.guardAura.id);
      else if (!guard && has) removeInstance(ctx, u, has, 'cancelled');
    }
    const target = pickTarget(ctx, u, owner, guard);
    if (!target) {
      u.targetId = null;
      u.inCombat = false;
      if (def.regenPct && u.hp < u.maxHp) u.hp = Math.min(u.maxHp, u.hp + Math.max(1, Math.round((u.maxHp * def.regenPct * TICK_MS) / 1000)));
      const heel = heelPoint(owner, u.id < owner.id ? 1 : -1);
      if (dist(u.pos, heel) > HEEL_SLACK) petGoTo(ctx, u, heel);
      else {
        petStop(u);
        if (!owner.moving) u.facing = owner.facing;
      }
      continue;
    }
    u.targetId = target.id;
    u.inCombat = true;
    // The owner fights what its beast fights (combat state, kill credit), without taking its aggro.
    if (target.threat && target.threat.entries[u.id] && !target.threat.entries[owner.id]) target.threat.entries[owner.id] = { threat: 0, lastAt: ctx.now };
    if (guard && def.guardAbilityId && ctx.now >= st.guardAt && target.targetId === owner.id) {
      st.guardAt = ctx.now + (def.guardEveryMs ?? 6000);
      ctx.sys.casting.executeAbility(ctx, u.id, def.guardAbilityId, target.id, null);
    }
    if (!ctx.sys.spatial.inMeleeRange(w, u.id, target.id)) {
      petGoTo(ctx, u, target.pos);
      continue;
    }
    petStop(u);
    u.facing = Math.atan2(target.pos.y - u.pos.y, target.pos.x - u.pos.x);
  }
  if (gone) for (const id of gone) ctx.ops.despawn(id);
}

/** Bound `u` toward `to` (a forced move, like a dash). */
function leap(ctx: SimContext, u: Unit, to: Vec2): void {
  const dest = ctx.sys.spatial.clampToWalkable(ctx.world, to);
  const d = dist(u.pos, dest);
  if (d < 0.5) return;
  u.move = { mode: 'forced', to: dest, speed: LEAP_SPEED, until: ctx.now + Math.max(TICK_MS, Math.round((d / LEAP_SPEED) * 1000)) };
  u.facing = Math.atan2(dest.y - u.pos.y, dest.x - u.pos.x);
}

/** `companionCast`: the caster's companion uses an ability now (effects.ts). */
export function companionCast(ctx: SimContext, e: CompanionCastEffect, ectx: EffectCtx): void {
  const comp = companionOf(ctx, ectx.sourceId);
  const owner = unitOf(ctx, ectx.sourceId);
  if (!comp || !owner) return;
  const ab = ctx.content.ability(e.abilityId);
  if (!ab) return;
  const t0 = unitOf(ctx, ectx.targetId);
  const t = t0?.alive && isHostile(comp, t0) ? t0 : unitOf(ctx, comp.targetId);
  const target = t?.alive && isHostile(comp, t) ? t : undefined;
  if (e.toOwner) leap(ctx, comp, heelPoint(owner, 1));
  else if (e.leap && target && !ctx.sys.spatial.inMeleeRange(ctx.world, comp.id, target.id)) {
    const reach = Math.max(MIN_MELEE_RANGE, comp.combatReach + target.combatReach + MELEE_RANGE_BONUS) - 1;
    const dx = comp.pos.x - target.pos.x;
    const dy = comp.pos.y - target.pos.y;
    const d = Math.hypot(dx, dy) || 1;
    leap(ctx, comp, { x: target.pos.x + (dx / d) * reach, y: target.pos.y + (dy / d) * reach });
  }
  if (target && !e.toOwner) {
    comp.targetId = target.id;
    comp.inCombat = true;
  }
  if (ab.targeting === 'enemy' && !target) return;
  // `toOwner`: the ability lands where the owner stands now (point), e.g. a taunt around the spot it bounds to.
  ctx.sys.casting.executeAbility(ctx, comp.id, ab.id, ab.targeting === 'enemy' ? target!.id : (target?.id ?? comp.id), e.toOwner ? { x: owner.pos.x, y: owner.pos.y } : null);
}

/**
 * Damage hook (damage.ts, after a hit landed): the owner's and its companion's direct hits on the
 * same target within the sync window give the owner the sync resource (Bond).
 */
export function companionSync(ctx: SimContext, s: Unit, t: Unit, req: DamageRequest, taken: number): void {
  if (req.periodic || taken <= 0 || !isHostile(s, t)) return;
  let comp: Unit | undefined;
  let owner: Unit | undefined;
  if (s.companion) {
    comp = s;
    owner = unitOf(ctx, s.companion.ownerId);
  } else if (s.kind === 'player') {
    owner = s;
    comp = companionOf(ctx, s.id);
  }
  if (!comp?.companion || !owner?.alive) return;
  const sync = companionDef(ctx, comp)?.sync;
  if (!sync || sync.excludeAbilityIds?.includes(req.abilityId)) return;
  const st = comp.companion;
  const now = ctx.now;
  let partnerOn: UnitId | null;
  let partnerAt: number;
  if (s === comp) {
    st.hitOn = t.id;
    st.hitAt = now;
    partnerOn = st.ownerHitOn;
    partnerAt = st.ownerHitAt;
  } else {
    st.ownerHitOn = t.id;
    st.ownerHitAt = now;
    partnerOn = st.hitOn;
    partnerAt = st.hitAt;
  }
  if (partnerOn !== t.id || now - partnerAt > sync.windowMs || now - st.lastSyncAt < sync.windowMs) return;
  st.lastSyncAt = now;
  changeResource(ctx, owner.id, sync.resource, sync.amount, comp.id, req.abilityId);
}
